/**
 * EmptyState
 *
 * The signature decorative-stacked-card empty state used across the app
 * (Keepsakes, Wishlist, Notifications, etc). Centralized here so every
 * "nothing here yet" surface has the same visual language: a soft pink +
 * blue back-tilt, a warm cream-orange front card with a centered icon, an
 * editorial heading, supporting copy, and optional CTAs.
 *
 * The `compact` variant shrinks everything by ~25%, use it when the empty
 * state sits inside a card section rather than as a full page (e.g. the
 * "no saved gifts yet" panel inside the recipient detail page).
 */

export default function EmptyState({
  icon: Icon,
  title,
  description,
  cta,
  secondaryCta,
  compact = false,
  className = '',
  darkMode,
  testId = 'empty-state',
}) {
  const wrapperPad = compact ? 'py-12' : 'py-20';
  const stackSize = compact ? 'w-36 h-36 mb-6' : 'w-48 h-48 mb-8';
  const iconSize = compact ? 'w-12 h-12' : 'w-16 h-16';
  const titleSize = compact ? 'text-2xl' : 'text-3xl';
  const titleMb = compact ? 'mb-3' : 'mb-4';
  const descSize = compact ? 'text-sm' : 'text-base';
  const descMb = compact ? 'mb-6' : 'mb-8';

  // Lean on dark mode tokens when provided; otherwise stick to the
  // light-mode palette the original components used (these empty states
  // also appear on public/shared pages that don't have access to the
  // useDarkMode hook).
  const titleColor = darkMode?.textPrimary || '#20212B';
  const descColor = darkMode?.textSecondary || '#534145';

  return (
    <div
      className={`text-center ${wrapperPad} ${className}`}
      data-testid={testId}
    >
      <div className="max-w-md mx-auto">
        <div className={`relative ${stackSize} mx-auto`}>
          <div
            className="absolute inset-4 rounded-2xl"
            style={{
              background:
                'linear-gradient(135deg, #E8B4B8 0%, #D4A4A8 100%)',
              transform: 'rotate(-5deg)',
            }}
          />
          <div
            className="absolute inset-4 rounded-2xl"
            style={{
              background:
                'linear-gradient(135deg, #B4D4E8 0%, #A4C4D8 100%)',
              transform: 'rotate(5deg)',
            }}
          />
          <div
            className="absolute inset-0 rounded-2xl flex items-center justify-center"
            style={{
              background:
                'linear-gradient(135deg, #FED7A5 0%, #E8C795 100%)',
            }}
          >
            {Icon && <Icon className={iconSize} style={{ color: 'white' }} />}
          </div>
        </div>

        <h2
          className={`${titleSize} ${titleMb} transition-colors`}
          style={{
            fontFamily: 'Playfair Display, serif',
            color: titleColor,
            fontWeight: 600,
          }}
        >
          {title}
        </h2>

        {description && (
          <p
            className={`${descSize} ${descMb} leading-relaxed transition-colors`}
            style={{ color: descColor }}
          >
            {description}
          </p>
        )}

        {(cta || secondaryCta) && (
          <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
            {cta && <CtaButton {...cta} primary />}
            {secondaryCta && <CtaButton {...secondaryCta} />}
          </div>
        )}
      </div>
    </div>
  );
}

function CtaButton({ label, onClick, href, icon: Icon, primary, testId }) {
  const baseStyle = primary
    ? {
        background: '#FED7A5',
        color: '#20212B',
        borderRadius: '30px',
        padding: '14px 28px',
        fontWeight: 600,
      }
    : {
        background: 'transparent',
        color: '#9E6752',
        border: '1.5px solid #9E6752',
        borderRadius: '30px',
        padding: '14px 28px',
        fontWeight: 600,
      };

  const className =
    'inline-flex items-center justify-center gap-2 transition-all hover:scale-105 hover:transform-none text-sm sm:text-base';

  // <a> when an href is supplied (works inside react-router via Link
  // wrapping); <button> otherwise. The caller decides.
  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={className}
        style={baseStyle}
        data-testid={testId}
      >
        {Icon && <Icon className="w-4 h-4" />}
        {label}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={className}
      style={baseStyle}
      data-testid={testId}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </button>
  );
}
